import axios from 'axios';

// Determine the base URL for API calls
const getBaseUrl = () => {
  // In Replit environment, use the proxy path for API calls
  // This will route through webpack dev server proxy to backend
  return '/api';
};

// Create an axios instance for our API
const api = axios.create({
  baseURL: getBaseUrl(),
  headers: {
    'Content-Type': 'application/json',
  },
  // Enable cookies for cross-origin requests (needed for auth)
  withCredentials: true,
  // Add timeout to prevent hanging requests
  timeout: 300000, // 5 minutes
});

// Add a request interceptor to add the auth token to every request
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Auth endpoints that should NOT trigger redirect on 401
const AUTH_ENDPOINTS = ['/auth/login', '/auth/register'];

// Add a response interceptor to handle common errors
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    const requestUrl = error.config?.url || '';
    const isAuthEndpoint = AUTH_ENDPOINTS.some(endpoint => requestUrl.includes(endpoint));
    
    if (error.response && error.response.status === 401 && !isAuthEndpoint) {
      localStorage.removeItem('token');
      localStorage.removeItem('userRole');
      // Dispatch a SPA-level event so the router can handle the redirect without
      // a hard page reload (which would wipe the browser history stack).
      // App.tsx listens for this event and calls navigate() within the SPA.
      window.dispatchEvent(
        new CustomEvent('auth:session-expired', {
          detail: { returnTo: window.location.pathname + window.location.search },
        })
      );
    }

    if (error.response && error.response.status === 403 && error.response.data) {
      const errorType = error.response.data.error;
      if (errorType === 'subscription_required' || errorType === 'feature_not_available' || errorType === 'usage_limit_reached' || errorType === 'subscription_inactive') {
        const data = error.response.data;
        window.dispatchEvent(
          new CustomEvent('subscription-upgrade-required', {
            detail: {
              type: errorType === 'subscription_required' || errorType === 'subscription_inactive' ? 'subscription_required' : errorType,
              featureKey: data.featureKey,
              featureDisplayName: data.featureDisplayName,
              requiredTier: data.requiredTier || 'starter',
              currentTier: data.currentTier || 'free',
              currentStatus: data.currentStatus || '',
              currentUsage: data.currentUsage,
              limit: data.limit,
              resetDate: data.resetDate,
              message: data.message,
            },
          })
        );
        error._subscriptionHandled = true;
      }
    }
    
    const errorMessage = 
      error.response && error.response.data && error.response.data.message
        ? error.response.data.message
        : error.message || 'An unknown error occurred';
    
    return Promise.reject({ 
      ...error, 
      message: errorMessage,
      _subscriptionHandled: error._subscriptionHandled || false,
    });
  }
);

export const wrapServiceError = (error: any, fallbackMessage: string): Error => {
  const message = error?.response?.data?.message || error?.message || fallbackMessage;
  const wrapped = new Error(message) as any;
  if (error?._subscriptionHandled) {
    wrapped._subscriptionHandled = true;
  }
  return wrapped;
};

export const uploadAvatar = async (file: File): Promise<{ avatarUrl: string; avatarSource: string }> => {
  const formData = new FormData();
  formData.append('avatar', file);
  
  const response = await api.post('/users/me/avatar', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  
  return response.data;
};

export const deleteAvatar = async (): Promise<void> => {
  await api.delete('/users/me/avatar');
};

export const deactivateAccount = () =>
  api.patch('/users/me/deactivate');

export const requestAccountDeletion = (reason?: string) =>
  api.post('/users/me/deletion-request', { reason });

export default api;